import { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { fetchPageCopy } from '../lib/copy';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

const CONTACT_DEFAULTS = {
  headline:     "Let's make something.",
  location:     'Lisbon, Portugal',
  availability: 'Available for remote and on-location projects globally',
};

const PROJECT_TYPES = [
  'Commercial Editing',
  'Creative Development',
  'Production Support',
  'Post Production Support',
  'Other',
];

const inp = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--rule)',
  padding: '0.85rem 0',
  fontSize: '14px',
  fontWeight: 300,
  color: 'var(--ink)',
  outline: 'none',
  transition: 'border-color 150ms',
  fontFamily: 'inherit',
};

export default function Contact() {
  const isMobile = useIsMobile();
  usePageTitle('Contact');
  const formRef = useRef();

  const [copy, setCopy]   = useState(CONTACT_DEFAULTS);
  const [form, setForm]   = useState({ name: '', company: '', email: '', projectType: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  useEffect(() => {
    fetchPageCopy('contact').then(c => {
      if (Object.keys(c).length > 0) setCopy(prev => ({ ...prev, ...c }));
    });
  }, []);

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY },
      );
      setStatus('success');
      setForm({ name: '', company: '', email: '', projectType: '', message: '' });
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="page-fade" style={{ paddingTop: '56px' }}>
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.5rem 5rem' : '5rem 3rem 8rem' }}>

        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '2rem' : '6rem',
          marginBottom: isMobile ? '3.5rem' : '6rem',
          paddingBottom: isMobile ? '3.5rem' : '6rem',
          borderBottom: '1px solid var(--rule)',
        }}>
          <div>
            <p style={{ fontSize: '11px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '2rem' }}>
              Contact
            </p>
            <h1 style={{ fontSize: isMobile ? '32px' : '52px', fontWeight: 300, lineHeight: 1.15, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              {copy.headline}
            </h1>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <a href="mailto:liam@liamboyd.com" style={{ fontSize: '16px', fontWeight: 300, color: 'var(--ink)', borderBottom: '1px solid var(--ink)', paddingBottom: '2px', alignSelf: 'flex-start', transition: 'opacity 150ms' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.5'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              liam@liamboyd.com
            </a>
            <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--ink-faint)' }}>{copy.location}</p>
            <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--ink-faint)' }}>{copy.availability}</p>
          </div>
        </div>

        {/* Form */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '0' : '6rem',
        }}>
          <div>
            {status === 'success' ? (
              <div style={{ padding: '2rem 0' }}>
                <p style={{ fontSize: '22px', fontWeight: 300, color: 'var(--ink)', marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>Message sent.</p>
                <p style={{ fontSize: '14px', fontWeight: 300, color: 'var(--ink-faint)', lineHeight: 1.7 }}>Thanks for getting in touch. I'll be in touch shortly.</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.25rem' }}>Name</label>
                    <input name="name" value={form.name} onChange={set('name')} required style={inp}
                      onFocus={e => e.target.style.borderBottomColor = 'var(--ink)'}
                      onBlur={e => e.target.style.borderBottomColor = 'var(--rule)'}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.25rem' }}>Company</label>
                    <input name="company" value={form.company} onChange={set('company')} style={inp}
                      onFocus={e => e.target.style.borderBottomColor = 'var(--ink)'}
                      onBlur={e => e.target.style.borderBottomColor = 'var(--rule)'}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.25rem' }}>Email</label>
                  <input name="email" type="email" value={form.email} onChange={set('email')} required style={inp}
                    onFocus={e => e.target.style.borderBottomColor = 'var(--ink)'}
                    onBlur={e => e.target.style.borderBottomColor = 'var(--rule)'}
                  />
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.25rem' }}>Project Type</label>
                  <select name="projectType" value={form.projectType} onChange={set('projectType')} style={{ ...inp, appearance: 'none', cursor: 'pointer' }}
                    onFocus={e => e.target.style.borderBottomColor = 'var(--ink)'}
                    onBlur={e => e.target.style.borderBottomColor = 'var(--rule)'}
                  >
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ fontSize: '10px', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.25rem' }}>Message</label>
                  <textarea name="message" value={form.message} onChange={set('message')} required rows={5} style={{ ...inp, resize: 'none', lineHeight: 1.7 }}
                    onFocus={e => e.target.style.borderBottomColor = 'var(--ink)'}
                    onBlur={e => e.target.style.borderBottomColor = 'var(--rule)'}
                  />
                </div>

                {status === 'error' && (
                  <p style={{ fontSize: '13px', fontWeight: 300, color: '#c0392b', marginTop: '0.5rem' }}>
                    Something went wrong. Please email liam@liamboyd.com directly.
                  </p>
                )}

                <button type="submit" disabled={status === 'sending'} style={{
                  marginTop: '1.5rem',
                  background: 'var(--ink)', color: 'white',
                  border: 'none', cursor: status === 'sending' ? 'default' : 'pointer',
                  padding: '0.9rem 2rem',
                  fontSize: '13px', fontWeight: 300, letterSpacing: '0.06em',
                  fontFamily: 'inherit',
                  opacity: status === 'sending' ? 0.6 : 1,
                  transition: 'opacity 150ms',
                  alignSelf: 'flex-start',
                }}>
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}
